import React, { useEffect, useRef, useState, createContext } from 'react';
import styles from './AdminPortal.module.css';
import ConnectWalletHeader from "../header/ConnectWalletHeader";

// General Tab Components
import FleetPurchaseInfoTable from './General/FleetPurchaseInfoTable';
import AdminWhitelistTable from "./General/AdminWhitelistTable";
import PurchaseRecordsTable from "./General/PurchaseRecordsTable";
import RobotTeleStatus from "./General/RobotTeleStatus";
import VerifyPaymentRecordsTable from "./General/VerifyPaymentRecordsTable";
import AdminInviteCodesTable from "./General/AdminInviteCodesTable";
import UserStatsTableV2 from './General/UserStatsTableV2';
// import UsersTable from "./General/UserTable";
// import UserStatsTable from "./General/UserStatsTable";
// import ReserveRobotTable from "./General/ReserveRobotTable";

// VLA Tab Components
import VLAAdminMachinesTable from "./VLA/VLAAdminMachinesTable";
import VLAAdminTasksTable from "./VLA/VLAAdminTasksTable";
import VLAAdminQAReviewers from './VLA/VLAAdminQAReviewers';
import VLAAdminHexCapabilityRequests from './VLA/VLAAdminHexCapabilityRequests';
import VLAAdminPartnerDeviceRequestsTable from './VLA/VLAAdminPartnerDeviceRequestsTable';
import DataUploadPauseControl from './VLA/DataUploadPauseControl';

// VLA Operators Tab Components
import VLAOperators from './VLAOperators/VLAOperators';
import JobsTable from './VLAOperators/JobsTable';

// Data Subscribers Tab Components
import ContactSalesSubmissionsTable from './DataSubscribers/ContactSalesSubmissionsTable';
import MarketingInterestAccessRequestsTable from './DataSubscribers/MarketingInterestAccessRequestsTable';
import EgocentricWaitlistRequestsTable from './DataSubscribers/EgocentricWaitlistRequestsTable';
import UniversityResearchRequestsTable from './DataSubscribers/UniversityResearchRequestsTable';
import ReferralsTable from './DataSubscribers/ReferralsTable';
import SubscribersInformation from './DataSubscribers/SubscribersInformation';

// Other Tab Components
import RoboticDataAdmin from '../RoboticData/RoboticDataAdmin';
import VideoDuplicateReviews from './VideoDuplicates/VideoDuplicateReviews';
import DatasetStatsAdmin from './DatasetStats/DatasetStatsAdmin';
import RoboticsDataBetaApplications from './RoboticsDataBeta/RoboticsDataBetaApplications';
import AdminQAReviewStats from './QAReview/AdminQAReviewStats';
import AdminEnterpriseDashboard from './Enterprise/AdminEnterpriseDashboard';
import OrderAdminDashboard from './Orders/OrderAdminDashboard';

import {ADMIN_SESSION_EXPIRED_EVENT, ADMIN_SESSION_REFRESHED_EVENT} from '../../utils/authErrors';


// Create a context for the admin portal
const AdminPortalContext = createContext();

// Sub-navigation section definitions for tabs with multiple stacked components
const GENERAL_SECTIONS = [
    { id: 'general-user-stats', label: 'User Stats' },
    { id: 'general-fleet-purchase', label: 'Fleet Purchase' },
    { id: 'general-whitelist', label: 'Whitelist' },
    { id: 'general-invite-codes', label: 'Invite Codes' },
    { id: 'general-purchase-records', label: 'Purchase Records' },
    { id: 'general-verify-payments', label: 'Verify Payments' },
    { id: 'general-tele-status', label: 'Teleop Control' },
];

const VLA_SECTIONS = [
    { id: 'vla-machines', label: 'Machines' },
    { id: 'vla-partner-devices', label: 'Partner Devices' },
    { id: 'vla-tasks', label: 'Tasks' },
    { id: 'vla-qa-reviewers', label: 'QA Reviewers' },
    { id: 'vla-hex-capability', label: 'Hex Capability' },
    { id: 'vla-upload-pause', label: 'Upload Control' },
];

const VLA_OPERATORS_SECTIONS = [
    { id: 'vla-op-operators', label: 'Operators' },
    { id: 'vla-op-jobs', label: 'Jobs' },
];

const DATA_SUBSCRIBERS_SECTIONS = [
    { id: 'data-sub-contact-sales', label: 'Contact Sales' },
    { id: 'data-sub-discover-researcher-interest', label: 'Discover/Researcher Interest' },
    { id: 'data-sub-egocentric-waitlist', label: 'Egocentric Waitlist' },
    { id: 'data-sub-university-research', label: 'University Research' },
    { id: 'data-sub-referrals', label: 'Referrals' },
    { id: 'data-sub-information', label: 'Subscribers’ Information' },
    { id: 'data-sub-pending', label: 'Pending Beta Access' },
]

const SUB_NAV_TABS = {
    general: GENERAL_SECTIONS,
    vla: VLA_SECTIONS,
    'vla-operators': VLA_OPERATORS_SECTIONS,
    'data-subscribers': DATA_SUBSCRIBERS_SECTIONS
};

// Main Admin Portal Component
const AdminPortal = () => {
    const [isWalletConnected, setIsWalletConnected] = useState(false);
    const [showPanel, setShowPanel] = useState(false);
    const [walletAddress, setWalletAddress] = useState(null);
    const [adminLogin, setAdminLogin] = useState(false);
    const [adminWhitelist] = useState([]);
    const[adminAccessToken,setAdminAccessToken] =useState('')
    const [activeTab, setActiveTab] = useState('general');
    const [subNavExpanded, setSubNavExpanded] = useState({
        general: true,
        vla: true,
        'vla-operators': true,
        'data-subscribers': true
    });
    const [activeSection, setActiveSection] = useState(GENERAL_SECTIONS[0].id);
    const sectionRefs = useRef({});
    const tabBarWrapperRef = useRef(null);

    useEffect(() => {
        const handleAdminSessionExpired = () => {
            localStorage.removeItem('prismax_admin_token');
            localStorage.removeItem('prismax_admin_refresh_token');
            setAdminLogin(false);
            setAdminAccessToken('');
        };
        const handleAdminSessionRefreshed = (event) => {
            const accessToken = String(event?.detail?.accessToken || '').trim();
            if (accessToken) setAdminAccessToken(accessToken);
        };
        window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleAdminSessionExpired);
        window.addEventListener(ADMIN_SESSION_REFRESHED_EVENT, handleAdminSessionRefreshed);
        return () => {
            window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, handleAdminSessionExpired);
            window.removeEventListener(ADMIN_SESSION_REFRESHED_EVENT, handleAdminSessionRefreshed);
        };
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
        const sections = SUB_NAV_TABS[activeTab];
        if (sections) {
            setActiveSection(sections[0].id);
        }
    }, [activeTab]);

    useEffect(() => {
        const sections = SUB_NAV_TABS[activeTab];
        if (!sections) return;

        const headerOffset = (tabBarWrapperRef.current ? tabBarWrapperRef.current.offsetHeight : 0) + 79;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.filter((entry) => entry.isIntersecting);
                if (visible.length === 0) return;
                const topMost = visible.reduce((a, b) =>
                    a.boundingClientRect.top < b.boundingClientRect.top ? a : b
                );
                setActiveSection(topMost.target.id);
            },
            {
                rootMargin: `-${headerOffset + 1}px 0px -50% 0px`,
                threshold: 0,
            }
        );

        sections.forEach(({ id }) => {
            const el = sectionRefs.current[id];
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [activeTab]);

    const toggleSubNav = (tab) => {
        setSubNavExpanded((prev) => ({ ...prev, [tab]: !prev[tab] }));
    };

    const scrollToSection = (id) => {
        setActiveSection(id);
        const el = sectionRefs.current[id];
        if (!el) return;
        const headerOffset = (tabBarWrapperRef.current ? tabBarWrapperRef.current.offsetHeight : 0) + 79 + 12;
        const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top, behavior: 'smooth' });
    };

    const renderSubNav = () => {
        const sections = SUB_NAV_TABS[activeTab];
        if (!sections) return null;
        const expanded = subNavExpanded[activeTab];
        return (
            <div className={styles.subNav}>
                <button
                    type="button"
                    className={styles.subNavToggle}
                    onClick={() => toggleSubNav(activeTab)}
                    aria-expanded={expanded}
                >
                    <span className={`${styles.subNavCaret} ${expanded ? styles.subNavCaretExpanded : ''}`}>
                        ▸
                    </span>
                    Sections
                </button>
                {expanded && (
                    <div className={styles.subNavList}>
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                type="button"
                                className={`${styles.subNavChip} ${activeSection === section.id ? styles.subNavChipActive : ''}`}
                                onClick={() => scrollToSection(section.id)}
                            >
                                {section.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <AdminPortalContext.Provider value={{ isWalletConnected }}>
            <div className={styles.adminPortal}>
                <ConnectWalletHeader
                    onWalletConnected={(publicKey) => {
                        setWalletAddress(publicKey);
                    }}
                    showPanel={showPanel}
                    setShowPanel={setShowPanel}
                    requireSignature={true}
                    adminWhitelist={adminWhitelist}
                    setAdminLogin={setAdminLogin}
                    setAdminAccessToken={setAdminAccessToken}
                />

                {adminLogin ? (
                    <div className={styles.mainContent}>
                        <div className={styles.tabBarWrapper} ref={tabBarWrapperRef}>
                        <div className={styles.tabBar}>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'general' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('general')}
                            >
                                General Admin
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'vla' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('vla')}
                            >
                                VLA Admin
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'vla-operators' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('vla-operators')}
                            >
                                VLA-Operators
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'data-subscribers' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('data-subscribers')}
                            >
                                Data Subscribers
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'dataset-stats' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('dataset-stats')}
                            >
                                Dataset Stats
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'data-download' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('data-download')}
                            >
                                Data Download
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'video-duplicates' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('video-duplicates')}
                            >
                                Video Duplicates
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'qa-review' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('qa-review')}
                            >
                                QA Review
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'enterprise' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('enterprise')}
                            >
                                Enterprise
                            </button>
                            <button
                                type="button"
                                className={`${styles.tabButton} ${activeTab === 'orders' ? styles.tabButtonActive : ''}`}
                                onClick={() => setActiveTab('orders')}
                            >
                                Orders
                            </button>
                        </div>
                        {renderSubNav()}
                        </div>

                        {activeTab === 'general' ? (
                            <>
                                {/* <UserStatsTable /> */}
                                {/* <UsersTable /> */}
                                <div id="general-user-stats" ref={(el) => { sectionRefs.current['general-user-stats'] = el; }}>
                                    <UserStatsTableV2 />
                                </div>
                                {/* <ReserveRobotTable /> */}
                                <div id="general-fleet-purchase" ref={(el) => { sectionRefs.current['general-fleet-purchase'] = el; }}>
                                    <FleetPurchaseInfoTable />
                                </div>
                                <div id="general-whitelist" ref={(el) => { sectionRefs.current['general-whitelist'] = el; }}>
                                    <AdminWhitelistTable/>
                                </div>
                                <div id="general-invite-codes" ref={(el) => { sectionRefs.current['general-invite-codes'] = el; }}>
                                    <AdminInviteCodesTable />
                                </div>
                                <div id="general-purchase-records" ref={(el) => { sectionRefs.current['general-purchase-records'] = el; }}>
                                    <PurchaseRecordsTable/>
                                </div>
                                <div id="general-verify-payments" ref={(el) => { sectionRefs.current['general-verify-payments'] = el; }}>
                                    <VerifyPaymentRecordsTable/>
                                </div>
                                <div id="general-tele-status" ref={(el) => { sectionRefs.current['general-tele-status'] = el; }}>
                                    <RobotTeleStatus/>
                                </div>
                            </>
                        ) : activeTab === 'vla' ? (
                            <>
                                <div id="vla-machines" ref={(el) => { sectionRefs.current['vla-machines'] = el; }}>
                                    <VLAAdminMachinesTable />
                                </div>
                                <div id="vla-partner-devices" ref={(el) => { sectionRefs.current['vla-partner-devices'] = el; }}>
                                    <VLAAdminPartnerDeviceRequestsTable />
                                </div>
                                <div id="vla-tasks" ref={(el) => { sectionRefs.current['vla-tasks'] = el; }}>
                                    <VLAAdminTasksTable />
                                </div>
                                <div id="vla-qa-reviewers" ref={(el) => { sectionRefs.current['vla-qa-reviewers'] = el; }}>
                                    <VLAAdminQAReviewers />
                                </div>
                                <div id="vla-hex-capability" ref={(el) => { sectionRefs.current['vla-hex-capability'] = el; }}>
                                    <VLAAdminHexCapabilityRequests adminToken={adminAccessToken} />
                                </div>
                                <div id="vla-upload-pause" ref={(el) => { sectionRefs.current['vla-upload-pause'] = el; }}>
                                    <DataUploadPauseControl adminToken={adminAccessToken} />
                                </div>
                            </>
                        ) : activeTab === 'vla-operators' ? (
                            <>
                                <div id="vla-op-operators" ref={(el) => { sectionRefs.current['vla-op-operators'] = el; }}>
                                    <VLAOperators />
                                </div>
                                <div id="vla-op-jobs" ref={(el) => { sectionRefs.current['vla-op-jobs'] = el; }}>
                                    <JobsTable />
                                </div>
                            </>
                        ) : activeTab === 'data-subscribers' ? (
                            <>
                                <div id="data-sub-contact-sales" ref={(el) => { sectionRefs.current['data-sub-contact-sales'] = el; }}>
                                    <ContactSalesSubmissionsTable />
                                </div>
                                <div id="data-sub-discover-researcher-interest" ref={(el) => { sectionRefs.current['data-sub-discover-researcher-interest'] = el; }}>
                                    <MarketingInterestAccessRequestsTable />
                                </div>
                                <div id="data-sub-egocentric-waitlist" ref={(el) => { sectionRefs.current['data-sub-egocentric-waitlist'] = el; }}>
                                    <EgocentricWaitlistRequestsTable />
                                </div>
                                <div id="data-sub-university-research" ref={(el) => { sectionRefs.current['data-sub-university-research'] = el; }}>
                                    <UniversityResearchRequestsTable />
                                </div>
                                <div id="data-sub-referrals" ref={(el) => { sectionRefs.current['data-sub-referrals'] = el; }}>
                                    <ReferralsTable />
                                </div>
                                <div id="data-sub-information" ref={(el) => { sectionRefs.current['data-sub-information'] = el; }}>
                                    <SubscribersInformation adminToken={adminAccessToken} />
                                </div>
                                <div id="data-sub-pending" ref={(el) => { sectionRefs.current['data-sub-pending'] = el; }}>
                                    <RoboticsDataBetaApplications adminToken={adminAccessToken} />
                                </div>
                            </>
                        ) : activeTab === 'dataset-stats' ? (
                            <DatasetStatsAdmin adminToken={adminAccessToken} />
                        ) : activeTab === 'data-download' ? (
                            <RoboticDataAdmin adminToken={adminAccessToken} />
                        ) : activeTab === 'video-duplicates' ? (
                            <VideoDuplicateReviews adminToken={adminAccessToken} />
                        ) : activeTab === 'qa-review' ? (
                            <AdminQAReviewStats adminToken={adminAccessToken} />
                        ) : activeTab === 'enterprise' ? (
                            <AdminEnterpriseDashboard />
                        ) : (
                            <OrderAdminDashboard adminToken={adminAccessToken} />
                        )}
                    </div>
                ) : (
                    <div className={styles.connectPrompt}>
                        <h2>Welcome to Admin Portal</h2>
                        <p>Please connect your wallet to access the admin features.</p>
                    </div>
                )}
            </div>
        </AdminPortalContext.Provider>
    );
};

export default AdminPortal;
